import React, { memo, useCallback, useEffect, useRef } from "react";
import { SwipeableItem } from "./swipeable-item";
import type {
  VocabularyWord,
  WordStatus,
} from "@/ear-reader/types/vocabulary-exercise";
import type { Logger } from "@bindery/logging";

// Types

export interface SwipeableVocabularyListProps {
  words: VocabularyWord[];
  onStatusChange: (id: number, status: WordStatus) => void;
  focused: boolean;
  activeIndex: number | null;
  reviewIndex: number | null;
  setReviewIndex: (
    value: number | null | ((prev: number | null) => number | null),
  ) => void;
  /** Whether the aide drawer is open (suppresses keyboard handling) */
  isDrawerOpen: boolean;
  /** Logger instance for structured telemetry */
  log: Logger;
}

// SwipeableVocabularyList

/**
 * Displays a list of swipeable word items in reverse order so that the most
 * recently reviewed word appears at the top. Supports keyboard navigation
 * (ArrowUp / ArrowDown) when in review mode.
 *
 * Wrapped in memo() to prevent unnecessary re-renders when parent state
 * changes but props remain equal.
 */
export const SwipeableVocabularyList = memo(function SwipeableVocabularyList({
  words,
  onStatusChange,
  focused,
  activeIndex,
  reviewIndex,
  setReviewIndex,
  isDrawerOpen,
  log,
}: SwipeableVocabularyListProps) {
  // --- Refs for keyboard handler ---
  // All prop values used inside the keyboard event handler are synced to refs
  // in a single consolidated effect. This keeps the keyboard listener stable
  // (attached once on mount) while always reading current values at event time.
  const setReviewIndexRef = useRef(setReviewIndex);
  const wordsLengthRef = useRef(words.length);
  const focusedRef = useRef(focused);
  const isDrawerOpenRef = useRef(isDrawerOpen);
  const reviewIndexRef = useRef(reviewIndex);

  // Sync all refs in a single effect
  useEffect(() => {
    setReviewIndexRef.current = setReviewIndex;
    wordsLengthRef.current = words.length;
    focusedRef.current = focused;
    isDrawerOpenRef.current = isDrawerOpen;
    reviewIndexRef.current = reviewIndex;
  }, [setReviewIndex, words.length, focused, isDrawerOpen, reviewIndex]);

  // --- Keyboard navigation (ArrowUp / ArrowDown) ---
  // Attached once on mount. Reads current values from refs at event time.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!focusedRef.current || reviewIndexRef.current === null) return;
      if (isDrawerOpenRef.current) return;

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setReviewIndexRef.current((prevIndex) =>
          prevIndex !== null ? Math.max(prevIndex - 1, 0) : null,
        );
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        setReviewIndexRef.current((prevIndex) =>
          prevIndex !== null
            ? Math.min(prevIndex + 1, wordsLengthRef.current - 1)
            : null,
        );
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // --- Visible words: filter then render ---
  // Reverse the list so most-recently-reviewed words appear at the top.
  const reversedWords = words.slice().reverse();

  const visibleWords =
    activeIndex !== null
      ? reversedWords.filter(
          (_word, index) => index > words.length - activeIndex - 1,
        )
      : [];

  // Pass index directly to onFocus to avoid creating closure factories per render
  const handleFocus = useCallback(
    (index: number) => {
      setReviewIndex(index);
    },
    [setReviewIndex],
  );

  return (
    <div className="space-y-2">
      {visibleWords.map((word, index) => (
        <SwipeableItem
          key={word.id}
          word={word}
          onStatusChange={onStatusChange}
          isFocused={reviewIndex === index}
          onFocus={() => handleFocus(index)}
          index={index}
          isDrawerOpen={isDrawerOpen}
          log={log}
        />
      ))}
    </div>
  );
});
